import { isNonEmpty } from '@sgme/fp';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { showNotificationThunk } from 'state/notifications/notification.thunk';
import {
  type ProductCodeType,
  SECONDARY_MARKET_PRODUCTS_BLOTTER_MAX_ITEMS,
} from 'state/secondaryMarket/productSearch/productSearch.model';
import { getSecondaryMarketProductSearchItems } from 'state/secondaryMarket/productSearch/productSearch.selectors';
import { secondaryMarketAstroProductFetchRequested } from 'state/secondaryMarket/referential/astroProduct/astroProduct.actions';
import { getIsUserInternal } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { configByCodeType } from 'utils/analytics';

import type { ProductChange, ProductChangeDone, ProductField } from './productSearch.actions';
import {
  secondaryMarketProductCodeSearchRequested,
  secondaryMarketProductRefreshRequested,
  secondaryMarketProductsCustodianOrClientChangedRequest,
  secondaryMarketProductsSalesMessageChangedRequest,
  secondaryMarketProductsValueChanged,
  secondaryMarketProductsValueChangedDone,
  secondaryMarketProductsValueChangedFailed,
  secondaryMarketProductsValueChangedRequest,
  secondaryMarketUnderlyingsSearchRequested,
} from './productSearch.actions';

export function SecondaryMarketProductCodeSearchRequestedThunk(
  codeType: ProductCodeType,
  codes: string[],
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    codes = codes.map(c => c.toLocaleUpperCase());
    const estimatedProductsCount = getSecondaryMarketProductSearchItems(state).length + codes.length;
    if (estimatedProductsCount > SECONDARY_MARKET_PRODUCTS_BLOTTER_MAX_ITEMS) {
      dispatch(
        showNotificationThunk({
          level: 'danger',
          title: { id: 'secondaryMarketProductSearch.tooMuchKeys.title' },
          content: [
            {
              id: 'secondaryMarketProductSearch.tooMuchKeys.message',
              values: {
                maxProductsCount: SECONDARY_MARKET_PRODUCTS_BLOTTER_MAX_ITEMS,
                productsCount: Math.max(SECONDARY_MARKET_PRODUCTS_BLOTTER_MAX_ITEMS - estimatedProductsCount, 0),
              },
            },
          ],
        }),
      );
    } else {
      logAnalytics.trackEvent('Secondary market', configByCodeType[codeType].action);
      const key = codes.map(configByCodeType[codeType].cvtToKey);
      dispatch(secondaryMarketProductCodeSearchRequested(key, key, false));
    }
  };
}
export function notifySecondaryMarketProductsSearchResultThunk({
  searchCount,
  foundCount,
}: {
  searchCount: number;
  foundCount: number;
}): Thunk<void> {
  return dispatch => {
    const missingCount = searchCount - foundCount;
    if (missingCount > 0) {
      dispatch(
        showNotificationThunk({
          level: 'warning',
          title: `${missingCount} missing codes`,
          content: [`some product codes were not found.`],
        }),
      );
    }
    if (foundCount > 0) {
      dispatch(
        showNotificationThunk({
          level: 'info',
          title: `${foundCount} products found`,
          content: ['The products have been added to the results grid'],
        }),
      );
    }
  };
}

export function secondaryMarketProductRefreshAllRequested(): Thunk<void> {
  return (
    dispatch,
    getState,
    {
      selectors: {
        fromSecondaryProductsSearchBlotter: { getSecondaryMarketProductSearchItemRowIds, getSecondaryMarketDlpRowIds },
      },
    },
  ) => {
    const state = getState();
    const keys = getSecondaryMarketProductSearchItemRowIds(state);
    const dlpKeys = getSecondaryMarketDlpRowIds(state);

    if (isNonEmpty(keys) || isNonEmpty(dlpKeys)) {
      dispatch(secondaryMarketProductRefreshRequested(keys, dlpKeys));
    }
  };
}

export function searchProductUnderlyingsThunk(rowId: string): Thunk<void> {
  return (
    dispatch,
    getState,
    {
      selectors: {
        fromSecondaryProductsSearchBlotter: { getSecondaryMarketProductSearchUnderlyingsKeys },
      },
    },
  ) => {
    const state = getState();
    const keys = getSecondaryMarketProductSearchUnderlyingsKeys(state, rowId);
    dispatch(secondaryMarketUnderlyingsSearchRequested(keys));
  };
}

export function secondaryMarketProductsValueChangedThunk(change: ProductChange): Thunk<void> {
  return dispatch => {
    if (change.field === 'salesMessage') {
      dispatch(secondaryMarketProductsValueChanged(change));
      dispatch(secondaryMarketProductsValueChangedRequest(change));
      dispatch(secondaryMarketProductsSalesMessageChangedRequest(change));
    }
    if (change.field === 'client' || change.field === 'custodian') {
      if (!change.isAsync) {
        dispatch(secondaryMarketProductsValueChanged(change));
      } else {
        dispatch(secondaryMarketProductsValueChangedRequest(change));
        dispatch(secondaryMarketProductsCustodianOrClientChangedRequest(change));
      }
    }
  };
}
export function secondaryMarketProductsValueChangedDoneThunk(
  fields: ProductChangeDone[],
  errors?: string,
  warnings?: string,
): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketProductsValueChangedDone(fields, errors, warnings));
    if (isBulkAction(fields)) {
      if (errors) {
        dispatch(
          showNotificationThunk({
            level: 'danger',
            title: { id: 'blotter.error.bulkEditError' },
            content: [errors],
            closeAutomatically: true,
          }),
        );
      }
      if (warnings) {
        dispatch(
          showNotificationThunk({
            level: 'warning',
            title: { id: 'blotter.error.bulkEditWarning' },
            content: [warnings],
            closeAutomatically: true,
          }),
        );
      }
    }
  };
}

export function secondaryMarketProductsValueChangedFailedThunk(fields: ProductField[], errors?: string): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketProductsValueChangedFailed(fields, errors));
    if (isBulkAction(fields) && !!errors) {
      dispatch(
        showNotificationThunk({
          level: 'danger',
          title: { id: 'blotter.error.bulkEditError' },
          content: [errors],
          closeAutomatically: true,
        }),
      );
    }
  };
}

function isBulkAction(changes: ProductField[]) {
  return changes.length > 1;
}

export function secondaryMarketAstroProductFetchRequestedThunk(isin: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    if (getIsUserInternal(state)) {
      logAnalytics.trackEvent('Secondary market', 'Open product details modal');
      dispatch(secondaryMarketAstroProductFetchRequested(isin));
    } else {
      logAnalytics.trackEvent('Secondary market', 'Open RT quote form');
      dispatch(secondaryMarketAstroProductFetchRequested(isin, undefined, true));
    }
  };
}
